import { DoctorType, QueryFactory, SortDirection } from '@lmt/shared-types';
import { DoctorListDto } from '@lmt/shared-types';
import { sortingQueryParams } from '@lmt/unicorn-shared/app/components/common/table/operations';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { SearchInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/SearchInput';
import { useInsertKeyHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useInsertKeyHandler';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import React, { FC, useCallback, useState } from 'react';
import {
  BooleanParam,
  StringParam,
  withDefault,
} from 'react-router-url-params';
import { DoctorsTable } from './components/DoctorsTable';

import styles from './DoctorsList.module.scss';
import { useDebouncedCallback } from 'use-debounce';

interface DoctorListProps {
  doctorType: DoctorType;
}

export const DoctorsList: FC<DoctorListProps> = React.memo(
  function DoctorsList(props) {
    const [queryParams, setQueryParams] = useQueryParamsInTab({
      search: withDefault(StringParam, null),
      isOnlyActive: withDefault(BooleanParam, true),
      ...sortingQueryParams(nameof<DoctorListDto>('name'), false),
    });

    const i18next = useScopedTranslation('doctors.list');
    const forms = useGlobalForms();

    const [totalCount, setTotalCount] = useState<number>(0);
    const [newItem, setNewItem] = useState<number>(0);

    const openDoctorCreatingForm = useCallback(async () => {
      const newDoc = await forms.openDoctorCreating(props.doctorType, true);
      setNewItem(newDoc?.id ?? 0);
    }, [forms]);

    // We need to use getDoctors because CustomStore cannot use react hooks in load section
    const getDoctors = useCallback(
      async (
        skip?: number,
        take?: number,
        sortingDirection?: SortDirection,
      ) => {
        const sortDirection =
          sortingDirection ??
          (queryParams.desc ? SortDirection.Desc : SortDirection.Asc);
        const data = await QueryFactory.DoctorClient.getDoctors(
          undefined,
          props.doctorType,
          undefined,
          undefined,
          queryParams.search?.toString(),
          queryParams.isOnlyActive,
          undefined,
          undefined,
          undefined,
          skip ?? 0,
          take ?? 100,
          nameof<DoctorListDto>('surname'),
          sortDirection,
          [
            {
              sortField: nameof<DoctorListDto>('name'),
              sortDirection: sortDirection,
            },
          ],
        );

        setTotalCount(data.totalCount);
        return {
          data: data.data,
          totalCount: data.totalCount,
        };
      },
      [queryParams],
    );

    const onSearchUpdated = useDebouncedCallback((value: string) => {
      setQueryParams({
        search: value,
      });
    }, 300);

    useInsertKeyHandler(openDoctorCreatingForm);

    return (
      <div
        className={styles.tableContainer}
        data-test-id="table-page-container"
      >
        <div className={styles.tableToolbar}>
          <TableTitle
            title={
              props.doctorType === DoctorType.Clinic
                ? i18next.t('attending_doctors_title')
                : i18next.t('external_doctors_title')
            }
            count={totalCount}
          />
          <div className={styles.searchField}>
            <SearchInput
              placeholder={i18next.t('search_placeholder')}
              bordered={true}
              onChange={(e) => onSearchUpdated(e.target.value)}
              onSearchIconClick={onSearchUpdated}
              onClear={onSearchUpdated}
              initialValue={queryParams.search}
              allowAnySearch
              blurOnEnter={false}
            />
          </div>
          <div className={styles.filters}>
            <CheckBox
              defaultChecked={queryParams.isOnlyActive}
              onClick={(event) => {
                setQueryParams({
                  isOnlyActive: event.currentTarget.checked,
                });
              }}
              title={i18next.t('table_filter_active')}
              name={`doctorSearchIsActive_${props.doctorType}`}
            />
            <Button
              className={styles.addButton}
              color={ButtonColor.Primary}
              iconComponent={<AddIcon />}
              onClick={openDoctorCreatingForm}
              title={
                props.doctorType === DoctorType.Clinic
                  ? i18next.t('add_attending_doctor_button')
                  : i18next.t('add_family_doctor_button')
              }
              testId={'plus-button'}
            />
          </div>
        </div>

        <div className={styles.doctorsTable}>
          <DoctorsTable
            key={newItem}
            getRows={getDoctors}
            doctorType={props.doctorType}
            queryParams={queryParams}
          />
        </div>
      </div>
    );
  },
);
